import { useState } from 'react';
import { useCerebroAPI } from '@/hooks/useCerebroAPI';
import { Volume2, Play, Pause, Mic } from 'lucide-react';

const VOICES = [
  { id: 'fiona', label: 'FIONA', description: 'Authority voice — warm, commanding' },
  { id: 'agent', label: 'AGENT', description: 'Operational voice — precise, neutral' },
  { id: 'oracle', label: 'ORACLE', description: 'Narrative voice — ethereal, resonant' },
  { id: 'custom', label: 'CUSTOM', description: 'User-defined voice profile' },
];

export function TTSControls() {
  const { sendTTS } = useCerebroAPI();
  const [text, setText] = useState('Welcome to CEREBRØ_X. All systems operational.');
  const [voice, setVoice] = useState('fiona');
  const [speed, setSpeed] = useState(1.0);
  const [pitch, setPitch] = useState(0.0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [lastAudioUrl, setLastAudioUrl] = useState<string | null>(null);

  const handleSynthesize = async () => {
    if (!text.trim()) return;
    setIsPlaying(true);
    const url = await sendTTS(text, voice, {
      speed,
      pitch,
      format: 'wav',
      post_process: { eq_preset: 'warm-vocal', compression: true, reverb: false },
    });
    if (url) {
      setLastAudioUrl(url);
      // Auto-play if it's a file URL
      if (url.startsWith('file://')) {
        // Cannot play file URLs from browser, show message
      } else {
        const audio = new Audio(url);
        audio.onended = () => setIsPlaying(false);
        audio.play().catch(() => setIsPlaying(false));
      }
    } else {
      setIsPlaying(false);
    }
  };

  return (
    <div className="bg-void/95 border border-border-subtle rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-surface/30">
        <Volume2 size={14} className="text-ice" />
        <span className="text-label text-ice">VOICE SYNTHESIS</span>
      </div>

      <div className="p-3 space-y-3">
        {/* Text Input */}
        <div>
          <label className="text-label text-tertiary mb-1 block">TEXT</label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            className="w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 text-sm text-primary placeholder:text-tertiary focus:border-ice focus:outline-none resize-none"
            placeholder="Enter text to synthesize..."
          />
        </div>

        {/* Voice Selection */}
        <div>
          <label className="text-label text-tertiary mb-1 block">VOICE PROFILE</label>
          <div className="grid grid-cols-2 gap-1.5">
            {VOICES.map((v) => (
              <button
                key={v.id}
                onClick={() => setVoice(v.id)}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md text-left transition-all ${
                  voice === v.id
                    ? 'bg-ice/10 border border-ice/40 text-ice'
                    : 'bg-surface/30 border border-transparent text-secondary hover:bg-hover'
                }`}
              >
                <Mic size={12} />
                <div>
                  <span className="text-mono-sm block">{v.label}</span>
                  <span className="text-[10px] text-tertiary">{v.description}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Speed & Pitch */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-label text-tertiary mb-1 block">SPEED {speed.toFixed(1)}x</label>
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={speed}
              onChange={(e) => setSpeed(Number(e.target.value))}
              className="w-full"
            />
          </div>
          <div>
            <label className="text-label text-tertiary mb-1 block">PITCH {pitch >= 0 ? '+' : ''}{pitch.toFixed(1)}</label>
            <input
              type="range"
              min="-1.0"
              max="1.0"
              step="0.1"
              value={pitch}
              onChange={(e) => setPitch(Number(e.target.value))}
              className="w-full"
            />
          </div>
        </div>

        {/* Post-Process */}
        <div className="flex items-center gap-3 pt-1">
          <label className="flex items-center gap-1.5 text-mono-sm text-secondary cursor-pointer">
            <input type="checkbox" defaultChecked className="accent-ice" />
            EQ
          </label>
          <label className="flex items-center gap-1.5 text-mono-sm text-secondary cursor-pointer">
            <input type="checkbox" defaultChecked className="accent-ice" />
            Compress
          </label>
          <label className="flex items-center gap-1.5 text-mono-sm text-secondary cursor-pointer">
            <input type="checkbox" className="accent-ice" />
            Reverb
          </label>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 pt-1">
          <button
            onClick={handleSynthesize}
            disabled={isPlaying || !text.trim()}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-ice/15 border border-ice/30 text-ice hover:bg-ice/25 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            {isPlaying ? <Pause size={14} /> : <Play size={14} />}
            <span className="text-mono-sm">{isPlaying ? 'SYNTHESIZING...' : 'SYNTHESIZE'}</span>
          </button>
          {lastAudioUrl && (
            <button
              onClick={() => {
                const audio = new Audio(lastAudioUrl);
                audio.play();
              }}
              className="p-2 rounded-lg bg-surface/50 text-secondary hover:text-primary hover:bg-hover transition-all"
              title="Replay"
            >
              <Play size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
